import React, { useMemo } from 'react'
import { Download } from 'lucide-react'
import { Accordion } from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { RelatedSelectionItem } from 'AdminPanel/Spine/editor/panel/selections/list-components/RelatedSelectionItem'
import { DownloadRelatedTemplateButton } from 'AdminPanel/Spine/editor/panel/selections/list-components/DownloadRelatedTemplateButton'
import { useConfigStore as useSpineConfigStore } from 'AdminPanel/Spine/store/configStore'

const PendingRelatedRow = ({ templateId }) => (
  <div className="flex shrink-0 items-center justify-between gap-2 rounded-lg border border-dashed border-muted-foreground/30 bg-muted/10 px-2.5 py-2">
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <Download className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
      <span className="truncate text-xs font-medium text-muted-foreground">
        关联: {templateId}
      </span>
      <Badge
        variant="outline"
        className="h-4 shrink-0 px-1 text-[10px] text-muted-foreground/70"
      >
        未加载
      </Badge>
    </div>
    <div className="shrink-0">
      <DownloadRelatedTemplateButton templateId={templateId} />
    </div>
  </div>
)

const RelatedSpineComponents = () => {
  const loadedRelatedTemplates = useSpineConfigStore(
    (s) => s.loadedRelatedTemplates,
  )
  const allRelatedTemplateIds = useSpineConfigStore(
    (s) => s.allRelatedTemplateIds,
  )

  const relatedGroups = useMemo(() => {
    if (!loadedRelatedTemplates?.length) return []
    return loadedRelatedTemplates.map((rel) => ({
      ...rel,
      selections: (rel.config?.selection || []).map((sel) => ({
        ...sel,
        id: `${rel.id}_${sel.id}`,
        originalId: sel.id,
        isRelated: true,
        relatedTemplateId: rel.id,
        skusMap: rel.config.skus,
      })),
    }))
  }, [loadedRelatedTemplates])

  const downloadedIds = useMemo(
    () => new Set(loadedRelatedTemplates?.map((r) => r.id) || []),
    [loadedRelatedTemplates],
  )

  const pendingIds = useMemo(
    () => (allRelatedTemplateIds || []).filter((id) => !downloadedIds.has(id)),
    [allRelatedTemplateIds, downloadedIds],
  )

  if (!allRelatedTemplateIds?.length) return null

  return (
    <div className="mt-2 border-t pt-2 pb-2">
      <div className="px-1 text-xs font-semibold text-muted-foreground">
        关联 Spine 组件
        <span className="ml-2 font-normal">预览区与 Spine 编辑器一致</span>
      </div>

      <div className="mt-2 flex flex-col gap-2 pr-1">
          {relatedGroups.map((group) => (
            <div key={group.id} className="flex shrink-0 flex-col gap-1">
              <div className="flex items-center justify-between rounded-sm bg-muted/20 px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                <span className="truncate">关联: {group.id}</span>
                <Badge variant="secondary" className="h-4 shrink-0 px-1 text-[10px]">
                  {group.selections.length}
                </Badge>
              </div>
              <Accordion type="single" collapsible className="flex flex-col gap-1">
                {group.selections.map((selection) => (
                  <RelatedSelectionItem
                    key={selection.id}
                    selection={selection}
                    spineData={group.spineData}
                  />
                ))}
              </Accordion>
            </div>
          ))}

          {pendingIds.map((id) => (
            <PendingRelatedRow key={id} templateId={id} />
          ))}
      </div>
    </div>
  )
}

export default RelatedSpineComponents
